import { useContext, useEffect, useRef, useState } from 'react';

import { useSubscription } from '@apollo/client';
import { Box, Flex, FlexProps, IconButton } from '@chakra-ui/react';
import { flushSync } from 'react-dom';
import { MdChatBubble, MdPhone } from 'react-icons/md';
import { RiMagicFill } from 'react-icons/ri';
import { toast } from 'react-toastify';

import { CustomerContactTypeEnum } from '../../gql/customerGql';
import { DealStateEnum, onAICloserCallStatus } from '../../gql/dealGql';
import {
  FollowUp,
  Maybe,
  StructuringFollowUp,
  useFollowUpQuery,
  useOnDealUpdateSubscription,
  useOnFollowUpUpdateSubscription,
  useOnStructuringFollowUpUpdateSubscription,
  useStructuringFollowUpQuery,
} from '../../gql/generated/graphql';

import CallWidget from '../CallWidget/CallWidget';
import AddFollowUp from '../FollowUp/AddFollowUp';
import EditFollowUp from '../FollowUp/EditFollowUp';
import NotificationsAvatar from '../NotificationBadge/NotificationAvatar';
import AddStructuringFollowUp from '../StructuringFollowUp/AddStructuringFollowUp';
import EditStructuringFollowUp from '../StructuringFollowUp/EditStructuringFollowUp';

import AiCallModal from './AiCallModal';

import { getUnseenMessageNotificationsCount } from '../../constants/notifications';
import { PermissionEnum } from '../../constants/permissions';
import { useUser } from '../../hooks/useUser';
import { Can } from '../../libs/Can';
import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { AbilityContext, TextWidgetContext } from '../../libs/contextLib';
import { formatPhone } from '../../libs/utils';
import { getConversationBoxes } from '../../utils/messages';

type CustomerContactProps = FlexProps & {
  inDealLockedModal?: boolean;
};
const CustomerContact = ({ inDealLockedModal = false, ...rest }: CustomerContactProps) => {
  const user = useUser();
  const { deal, dispatch, setUserChangedSomething } = useContext(DealContext);
  const hasPhone = !!(deal.customer.phone_number || deal.customer.home_phone_number);
  const buyerBox = getConversationBoxes({ deal }).find(
    (box) => box.role === CustomerContactTypeEnum.Customer,
  );
  const { setActiveConversation, setTextDealId, unreadCount, setUnreadCount } =
    useContext(TextWidgetContext);
  const abilities = useContext(AbilityContext);

  const callWidgetRef = useRef<HTMLDivElement>(null);
  const [callWidgetOpen, setCallWidgetOpen] = useState(false);
  const [followUp, setFollowUp] = useState<Maybe<FollowUp>>();
  const [structuringFollowUp, setStructuringFollowUp] = useState<Maybe<StructuringFollowUp>>();
  const [aiCallModalOpen, setAiCallModalOpen] = useState(false);

  const canUseAiCloser = abilities.has(PermissionEnum.UseAiCloser);
  const canSeeStructuringQueue =
    [
      DealStateEnum.Estimate,
      DealStateEnum.SoftClose,
      DealStateEnum.Structuring,
      DealStateEnum.StructuringInProgress,
    ].includes(deal.state) && abilities.has(PermissionEnum.SeeStructuringQueue);

  useFollowUpQuery({
    skip: !deal.id,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variables: { deal_id: deal.id! },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setFollowUp(data.followUp);
    },
  });

  useStructuringFollowUpQuery({
    skip: !canSeeStructuringQueue || !deal.id,
    variables: { dealId: deal.id ?? 0 },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setStructuringFollowUp(data.structuringFollowUp);
    },
  });

  useEffect(() => {
    dispatch({
      type: DealActionsEnum.UpdateDeal,
      payload: {
        follow_up: followUp,
      },
    });
  }, [followUp]);

  const dealPhoneNumbers = [formatPhone(deal.customer.phone_number)];
  if (deal.cobuyer) {
    dealPhoneNumbers.push(formatPhone(deal.cobuyer.phone_number));
  }
  if (deal.contact && deal.contact.phone_number) {
    dealPhoneNumbers.push(formatPhone(deal.contact.phone_number));
  }
  if (deal.second_contact && deal.second_contact.phone_number) {
    dealPhoneNumbers.push(formatPhone(deal.second_contact.phone_number));
  }

  useOnDealUpdateSubscription({
    skip: !deal.source || !deal.id,
    variables: {
      // DIEGO - DEBUG: source doesn't matter. It will filter by dealId.
      sources: [deal.source ?? ''],
      dealIds: [deal.id ?? -1],
    },
    onData: ({ data }) => {
      if (!data.data?.onDealUpdate?.length) {
        return;
      }

      const updatedDeal = data.data.onDealUpdate.find((d) => d?.id === deal.id);
      if (!updatedDeal) {
        return;
      }

      const stateChanged = updatedDeal.state && updatedDeal.state !== deal.state;
      const unseenNotificationsChanged =
        getUnseenMessageNotificationsCount(user.id, updatedDeal) !==
        getUnseenMessageNotificationsCount(user.id, deal);

      if (stateChanged || unseenNotificationsChanged) {
        // Prevent autosave from saving the deal when the state changes.
        // Use `flushSync` to ensure the state update happens synchronously before dispatch.
        flushSync(() => {
          setUserChangedSomething(false);
        });

        // This will trigger the recalculation of the initial values to avoid overriding with old values in `useCreditAppInitialValues` and `DealInfoBuyout`.
        dispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: {
            state: updatedDeal.state as DealStateEnum,
            notifications: updatedDeal.notifications?.filter((n) => n !== null && n !== undefined),
          },
        });
      }
    },
  });

  useOnFollowUpUpdateSubscription({
    variables: { dealId: deal.id ?? -1 },
    onData: ({ data: { data } }) => {
      if (!data?.onFollowUpUpdate?.[0]) {
        return;
      }
      setFollowUp(data.onFollowUpUpdate[0].follow_up);
    },
  });

  useOnStructuringFollowUpUpdateSubscription({
    skip: !canSeeStructuringQueue || !deal.id,
    variables: { dealId: deal.id ?? 0 },
    onData: ({ data: { data } }) => {
      setStructuringFollowUp(data?.onStructuringFollowUpUpdate?.[0]?.structuring_follow_up);
    },
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any) {
      if (callWidgetRef.current && !callWidgetRef.current.contains(event.target)) {
        setCallWidgetOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [callWidgetRef]);

  useEffect(() => {
    setUnreadCount(
      getUnseenMessageNotificationsCount(user.id, { notifications: deal.notifications }),
    );
  }, [user.id, deal.notifications]);

  const callCustomer = () => {
    setCallWidgetOpen(!callWidgetOpen);
  };

  useSubscription(onAICloserCallStatus, {
    variables: { dealId: deal.id },
    // Only need the subscription if the user has UseAiCloser since it triggers toasts related to the AI call
    skip: canUseAiCloser && !deal.id,
    onData: ({ data }) => {
      if (!data.data?.onAICloserCallStatus) {
        return;
      }

      const { event, reason, callSummary, inVoicemail, userSentiment, callSuccessful } =
        data.data.onAICloserCallStatus;

      switch (event) {
        case 'call_started':
          toast.success('Call Started');
          break;
        case 'call_ended':
          toast.info(`Call Ended: ${reason?.toUpperCase()}`);
          break;
        case 'call_analyzed':
          toast.info(
            `Call Analysis Complete:\n` +
              `Summary: ${callSummary}\n` +
              `In Voicemail: ${inVoicemail ? 'Yes' : 'No'}\n` +
              `Sentiment: ${userSentiment}\n` +
              `Successful: ${callSuccessful ? 'Yes' : 'No'}`,
          );
          break;
        default:
          break;
      }
    },
  });

  return (
    <>
      <Flex
        {...(!inDealLockedModal && {
          borderTopColor: 'gray.100',
          borderTopWidth: '2px',
          pt: '1rem',
          wrap: 'wrap',
        })}
        gap={4}
        justify="center"
        {...rest}
      >
        <Box position="relative">
          <IconButton
            aria-label="set active conversation"
            rounded="full"
            size="lg"
            variant="secondary"
            isDisabled={!buyerBox}
            onClick={() => {
              if (!buyerBox) {
                return;
              }
              setActiveConversation(buyerBox.conversation);
              if (deal.id) {
                setTextDealId(deal.id);
              }
            }}
            icon={<MdChatBubble size={24} />}
          />
          <NotificationsAvatar
            count={unreadCount}
            maxDisplayCount={9}
            size="xs"
            pos="absolute"
            top={-1.5}
            right={-1.5}
          />
        </Box>

        <Box
          ref={callWidgetRef}
          alignItems="center"
          display="flex"
          justifyContent="center"
          flexDirection="column"
          pos="relative"
        >
          <Box pos="relative">
            <IconButton
              aria-label="call customer"
              rounded="full"
              size="lg"
              variant="secondary"
              isDisabled={!hasPhone}
              onClick={callCustomer}
              icon={<MdPhone size={24} />}
            />
          </Box>
          <CallWidget widgetOpen={callWidgetOpen} toggleCallWidget={callCustomer} />
        </Box>
        <Can I={PermissionEnum.UseAiCloser}>
          <Box pos="relative">
            <IconButton
              aria-label="AI Assisted Call"
              rounded="full"
              size="lg"
              variant="secondary"
              onClick={() => setAiCallModalOpen(true)}
              icon={
                <Box display="flex" alignItems="center" gap="2px">
                  <MdPhone size={16} />
                  <RiMagicFill size={18} />
                </Box>
              }
            />
          </Box>
        </Can>

        {!inDealLockedModal && (
          <>
            {followUp ? <EditFollowUp followUp={followUp} /> : <AddFollowUp />}

            {canSeeStructuringQueue ? (
              structuringFollowUp ? (
                <EditStructuringFollowUp structuringFollowUp={structuringFollowUp} />
              ) : (
                <AddStructuringFollowUp />
              )
            ) : null}
          </>
        )}
      </Flex>

      {canUseAiCloser && deal.id && (
        <AiCallModal
          isOpen={aiCallModalOpen}
          onClose={() => setAiCallModalOpen(false)}
          dealId={deal.id.toString()}
          calledNumber={deal.customer.phone_number}
        />
      )}
    </>
  );
};

export default CustomerContact;
